import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { log } from '@/lib/logger';
import type { TeamBillingInfo } from '@overlayqa/shared';
import { useAppSession } from './useAppSession';
import { fetchApi } from '../lib/api/client';
import { useRetainedLoadError } from './useRetainedLoadError';

/**
 * Shared billing info hook. Multiple surfaces (BillingPage, TrialNudgeBanner,
 * Layout redirect logic) all read from one TanStack Query key so they share
 * the cache instead of fan-out-fetching.
 *
 * Query is enabled by default. Tune staleTime so the banner reflects upgrades
 * within a minute of a Clerk webhook firing.
 */
export function useBillingInfo() {
  const { getToken } = useAuth();
  const { data: session } = useAppSession();
  const teamId = session?.team?.id;
  const query = useQuery<TeamBillingInfo>({
    queryKey: ['billing', 'plan', teamId, session?.user?.id],
    enabled: !!teamId,
    queryFn: async () => {
      log.info('[useBillingInfo] fetching');
      const token = await getToken();
      return fetchApi<TeamBillingInfo>(`/billing/plan?teamId=${encodeURIComponent(teamId!)}`, { token: token ?? undefined });
    },
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
  const deadline = query.data?.betaExpiresAt;
  const refetch = query.refetch;
  useEffect(() => {
    if (!deadline) return;
    const expiresAt = new Date(deadline).getTime();
    if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) return;
    let timer: number;
    // A long invite can exceed the browser timer limit. Re-arm the timer
    // itself: an unchanged query response would not re-run this effect.
    const arm = () => {
      const delay = expiresAt - Date.now();
      timer = window.setTimeout(delay > 2_147_483_647 ? arm : () => { void refetch(); }, Math.min(Math.max(delay, 0), 2_147_483_647));
    };
    arm();
    return () => window.clearTimeout(timer);
  }, [deadline, refetch, teamId]);
  const error = useRetainedLoadError(query.error, query.isFetching, `${teamId}:${session?.user?.id}`);
  return { ...query, teamId, error, isLoading: query.isLoading && !error };
}
